import { useEffect, useState } from 'react';
import { Input, type InputProps } from '@/components/ui/input';
import { formatDurationInput, parseDuration } from './entry-form-model';

interface DurationInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  /** Seconds, or null while the typed text is not a valid duration. */
  value: number | null;
  onChange: (seconds: number | null) => void;
}

/**
 * Text duration in H:MM. Shows the value rounded to the minute and only writes the state when the
 * parsed value differs from what is shown, so an untouched timer entry keeps its exact seconds.
 */
export function DurationInput({ value, onChange, ...props }: DurationInputProps): JSX.Element {
  const [text, setText] = useState(value === null ? '' : formatDurationInput(value));

  // External changes (an End edit recomputes the duration) re-sync the text.
  useEffect(() => {
    if (value === null) return;
    setText((current) => {
      const parsed = parseDuration(current);
      return parsed.ok && formatDurationInput(parsed.seconds) === formatDurationInput(value) ? current : formatDurationInput(value);
    });
  }, [value]);

  function handleChange(next: string): void {
    setText(next);
    const parsed = parseDuration(next);
    if (!parsed.ok) {
      onChange(null);
      return;
    }
    if (value !== null && formatDurationInput(value) === formatDurationInput(parsed.seconds)) return;
    onChange(parsed.seconds);
  }

  return <Input {...props} type="text" inputMode="numeric" placeholder="H:MM" value={text} onChange={(e) => handleChange(e.target.value)} />;
}
